import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usersManagementAPI, handleApiError } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import StatCard, { StatGrid } from '../components/StatCard';
import { showSuccess, showError } from '../utils/toast';
import type {
  ReportedUser,
  AdminUserSearchItem,
  ModerationStatus,
  UserDetails,
  UserAnalytics,
  ModerationAction,
  ActionModal,
  ActionModalConfig,
  ModalVariant,
} from '../types';
import './Dashboard.css';
import './UsersManagementPage.css';

const SEARCH_DELAY_MS = 300;
const SEARCH_MIN_LENGTH = 2;

const formatDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString('bg-BG') : '-';

const UsersManagementPage: React.FC = () => {
  const [reportedUsers, setReportedUsers] = useState<ReportedUser[]>([]);
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<ReportedUser | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<AdminUserSearchItem[]>([]);
  const [resultsTotal, setResultsTotal] = useState<number>(0);
  const [searching, setSearching] = useState<boolean>(false);
  const searchRequest = useRef(0);

  // Modal states
  const [actionModal, setActionModal] = useState<ActionModal>({
    isOpen: false,
    action: null,
    user: null,
    reason: '',
    duration: 72,
  });
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const loadReportedUsers = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await usersManagementAPI.getMostReportedUsers(50);
      setReportedUsers(response.data?.users || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // The summary is extra: when it fails, the page still shows the reports.
  const loadUserAnalytics = useCallback(async (): Promise<void> => {
    try {
      const response = await usersManagementAPI.getUserAnalytics();
      setUserAnalytics(response.data);
    } catch (err) {
      console.error('Failed to load user analytics:', err);
    }
  }, []);

  useEffect(() => {
    loadReportedUsers();
    loadUserAnalytics();
  }, [loadReportedUsers, loadUserAnalytics]);

  // Typing waits a moment before it asks the backend.
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Only the latest search may fill the list; a slower older answer is dropped.
  const runSearch = useCallback(async (text: string): Promise<void> => {
    const request = ++searchRequest.current;
    if (text.length < SEARCH_MIN_LENGTH) {
      setResults([]);
      setResultsTotal(0);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const response = await usersManagementAPI.searchUsers(text);
      if (request !== searchRequest.current) return;
      setResults(response.data.items);
      setResultsTotal(response.data.total);
    } catch (err) {
      if (request === searchRequest.current) showError('Грешка при търсене: ' + handleApiError(err));
    } finally {
      if (request === searchRequest.current) setSearching(false);
    }
  }, []);

  useEffect(() => {
    runSearch(query);
  }, [query, runSearch]);

  const isSearching = query.length >= SEARCH_MIN_LENGTH;
  const listedUsers: ReportedUser[] = isSearching ? results : reportedUsers;
  const selectedSearchItem = results.find((item) => item.id === selectedUser?.id);

  const loadUserDetails = async (userId: string): Promise<void> => {
    setDetailsLoading(true);
    try {
      const [statusRes, breakdownRes] = await Promise.all([
        usersManagementAPI.getUserModerationStatus(userId),
        usersManagementAPI.getUserReportsBreakdown(userId),
      ]);
      setUserDetails({
        status: statusRes.data,
        breakdown: breakdownRes.data,
      });
    } catch (err) {
      showError('Грешка при зареждане на детайли: ' + handleApiError(err));
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSelectUser = (user: ReportedUser): void => {
    setSelectedUser(user);
    loadUserDetails(user.id);
  };

  const handleAction = async (): Promise<void> => {
    const { action, user, reason, duration } = actionModal;
    if (!user || !action) return;

    if (!reason.trim()) {
      showError('Моля, въведете причина');
      return;
    }

    setActionLoading(true);
    try {
      switch (action) {
        case 'ban':
          await usersManagementAPI.banUser(user.id, reason);
          showSuccess(`${user.first_name} ${user.last_name} беше баннат`);
          break;
        case 'unban':
          await usersManagementAPI.unbanUser(user.id, reason);
          showSuccess(`Банът на ${user.first_name} ${user.last_name} беше премахнат`);
          break;
        case 'suspend':
          await usersManagementAPI.suspendUser(user.id, reason, duration);
          showSuccess(`${user.first_name} ${user.last_name} беше суспендиран за ${duration} часа`);
          break;
        case 'removeSuspension':
          await usersManagementAPI.removeSuspension(user.id, reason);
          showSuccess(`Суспендирането на ${user.first_name} ${user.last_name} беше премахнато`);
          break;
        case 'warn':
          await usersManagementAPI.warnUser(user.id, reason);
          showSuccess(`Предупреждение беше изпратено на ${user.first_name} ${user.last_name}`);
          break;
        case 'resetWarnings':
          await usersManagementAPI.resetWarnings(user.id, reason);
          showSuccess(`Предупрежденията на ${user.first_name} ${user.last_name} бяха нулирани`);
          break;
        default:
          break;
      }
      setActionModal({ isOpen: false, action: null, user: null, reason: '', duration: 72 });
      loadReportedUsers();
      if (isSearching) runSearch(query);
      if (selectedUser?.id === user.id) {
        loadUserDetails(user.id);
      }
    } catch (err) {
      showError('Грешка: ' + handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const openActionModal = (action: ModerationAction, user: ReportedUser): void => {
    setActionModal({
      isOpen: true,
      action,
      user,
      reason: '',
      duration: 72,
    });
  };

  const getActionModalConfig = (): ActionModalConfig => {
    const { action, user } = actionModal;
    const userName = user ? `${user.first_name} ${user.last_name}` : '';

    const configs: Record<ModerationAction, ActionModalConfig> = {
      ban: {
        title: 'Баниране на потребител',
        message: `Сигурни ли сте, че искате да баннете ${userName}? Това е перманентно действие.`,
        confirmText: 'Банирай',
        variant: 'danger',
      },
      unban: {
        title: 'Премахване на бан',
        message: `Премахване на бана от ${userName}?`,
        confirmText: 'Премахни бан',
        variant: 'default',
      },
      suspend: {
        title: 'Суспендиране на потребител',
        message: `Временно суспендиране на ${userName}?`,
        confirmText: 'Суспендирай',
        variant: 'warning',
      },
      removeSuspension: {
        title: 'Премахване на суспендиране',
        message: `Премахване на суспендирането от ${userName}?`,
        confirmText: 'Премахни',
        variant: 'default',
      },
      warn: {
        title: 'Предупреждение',
        message: `Изпращане на предупреждение до ${userName}?`,
        confirmText: 'Изпрати',
        variant: 'warning',
      },
      resetWarnings: {
        title: 'Нулиране на предупреждения',
        message: `Нулиране на всички предупреждения за ${userName}?`,
        confirmText: 'Нулирай',
        variant: 'default',
      },
    };

    return action ? configs[action] : { title: '', message: '', confirmText: '', variant: 'default' };
  };

  const getModerationStatusBadge = (status: ModerationStatus | undefined): React.ReactNode => {
    if (!status) return null;
    if (status.is_banned) return <span className="status-badge banned">Баннат</span>;
    if (status.is_suspended) return <span className="status-badge suspended">Суспендиран</span>;
    if (status.warning_count > 0) return <span className="status-badge warned">Предупреждения: {status.warning_count}</span>;
    return <span className="status-badge active">Активен</span>;
  };

  const modalConfig = getActionModalConfig();

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Управление на потребители</h2>
          <p className="last-updated">Преглед и модерация на потребители</p>
        </div>
        <button
          className={`floating-refresh ${loading ? 'loading' : ''}`}
          onClick={() => { loadReportedUsers(); loadUserAnalytics(); }}
          disabled={loading}
          title="Обновяване"
        >
          <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {/* User Analytics Summary */}
      {userAnalytics && (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-chart-pie"></i>
            Обобщение
          </h3>
          <StatGrid>
            <StatCard label="Общо потребители" icon="fas fa-users" tone="brand" value={userAnalytics.total_users || 0} />
            <StatCard label="Верифицирани" icon="fas fa-user-shield" tone="success" value={userAnalytics.verified_users || 0} />
            <StatCard label="Активни (7 дни)" icon="fas fa-user-check" tone="brand" value={userAnalytics.active_users || 0} />
            <StatCard
              label="Докладвани"
              icon="fas fa-flag"
              tone={reportedUsers.length > 0 ? 'error' : 'neutral'}
              value={reportedUsers.length}
            />
          </StatGrid>
        </div>
      )}

      <div className="users-layout">
        {/* Reported Users List */}
        <div className="users-list-section">
          <div className="section">
            <label className="users-search">
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                className="bo-input"
                placeholder="Име, имейл или телефон"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Търсене на потребител"
              />
            </label>

            <h3 className="section-title">
              <i className={isSearching ? 'fas fa-search' : 'fas fa-exclamation-triangle'}></i>
              {isSearching ? 'Резултати' : 'Най-докладвани потребители'}
              <span className="section-count">({isSearching ? resultsTotal : reportedUsers.length})</span>
            </h3>
            {isSearching && resultsTotal > results.length && (
              <p className="users-search-hint">
                Показани са първите {results.length}. Уточнете търсенето.
              </p>
            )}

            {(isSearching ? searching : loading) ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Зареждане...</p>
              </div>
            ) : listedUsers.length === 0 ? (
              <div className="empty-state">
                <i className={isSearching ? 'fas fa-user-slash' : 'fas fa-check-circle'}></i>
                <p>{isSearching ? 'Няма намерени потребители' : 'Няма докладвани потребители'}</p>
              </div>
            ) : (
              <div className="users-list">
                {listedUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`user-card ${selectedUser?.id === user.id ? 'selected' : ''}`}
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="user-avatar">
                      <i className="fas fa-user"></i>
                    </div>
                    <div className="user-info">
                      <div className="user-name">
                        {user.first_name} {user.last_name}
                        {user.is_banned && <span className="status-badge banned">Баннат</span>}
                        {!user.is_banned && user.is_suspended && (
                          <span className="status-badge suspended">Суспендиран</span>
                        )}
                      </div>
                      <div className="user-email">{user.email}</div>
                    </div>
                    <div className="user-reports">
                      <span className="report-count">{user.report_count}</span>
                      <span className="report-label">доклади</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* User Details Panel */}
        <div className="user-details-section">
          {selectedUser ? (
            <div className="section">
              <h3 className="section-title">
                <i className="fas fa-user-cog"></i>
                Детайли за потребителя
              </h3>

              {detailsLoading ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Зареждане на детайли...</p>
                </div>
              ) : (
                <div className="user-details-content">
                  <div className="detail-header">
                    <div className="detail-avatar">
                      <i className="fas fa-user"></i>
                    </div>
                    <div className="detail-info">
                      <h4>{selectedUser.first_name} {selectedUser.last_name}</h4>
                      <p>{selectedUser.email}</p>
                      {userDetails?.status && getModerationStatusBadge(userDetails.status)}
                    </div>
                  </div>

                  {userDetails?.status && (
                    <div className="moderation-info">
                      <h5>Модерационен статус</h5>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">Предупреждения:</span>
                          <span className="info-value">{userDetails.status.warning_count || 0}</span>
                        </div>
                        {userDetails.status.is_suspended && userDetails.status.suspended_until && (
                          <div className="info-item">
                            <span className="info-label">Суспендиран до:</span>
                            <span className="info-value">
                              {new Date(userDetails.status.suspended_until).toLocaleString('bg-BG')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedSearchItem && (
                    <div className="moderation-info">
                      <h5>Профил</h5>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">Телефон:</span>
                          <span className="info-value">{selectedSearchItem.phone_number || '-'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Имейл потвърден:</span>
                          <span className="info-value">{selectedSearchItem.is_email_verified ? 'Да' : 'Не'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Регистриран:</span>
                          <span className="info-value">{formatDate(selectedSearchItem.created_at)}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Последен вход:</span>
                          <span className="info-value">{formatDate(selectedSearchItem.last_login)}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Доклади:</span>
                          <span className="info-value">{selectedSearchItem.report_count}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {userDetails?.breakdown && (
                    <div className="reports-breakdown">
                      <h5>Разбивка на докладите</h5>
                      <div className="breakdown-grid">
                        {Object.entries(userDetails.breakdown.by_reason || {}).map(([reason, count]) => (
                          <div key={reason} className="breakdown-item">
                            <span className="breakdown-reason">{reason}</span>
                            <span className="breakdown-count">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="moderation-actions">
                    <h5>Действия</h5>
                    <div className="action-buttons">
                      {!userDetails?.status?.is_banned && (
                        <>
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => openActionModal('warn', selectedUser)}
                          >
                            <i className="fas fa-exclamation-circle"></i> Предупреди
                          </button>
                          <button
                            className="btn btn-orange btn-sm"
                            onClick={() => openActionModal('suspend', selectedUser)}
                          >
                            <i className="fas fa-clock"></i> Суспендирай
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => openActionModal('ban', selectedUser)}
                          >
                            <i className="fas fa-ban"></i> Банирай
                          </button>
                        </>
                      )}
                      {userDetails?.status?.is_banned && (
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => openActionModal('unban', selectedUser)}
                        >
                          <i className="fas fa-check"></i> Премахни бан
                        </button>
                      )}
                      {userDetails?.status?.is_suspended && !userDetails?.status?.is_banned && (
                        <button
                          className="btn btn-info btn-sm"
                          onClick={() => openActionModal('removeSuspension', selectedUser)}
                        >
                          <i className="fas fa-undo"></i> Премахни суспендиране
                        </button>
                      )}
                      {(userDetails?.status?.warning_count ?? 0) > 0 && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openActionModal('resetWarnings', selectedUser)}
                        >
                          <i className="fas fa-eraser"></i> Нулирай предупреждения
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="section">
              <div className="empty-details">
                <i className="fas fa-user-shield"></i>
                <p>Изберете потребител от списъка за преглед на детайли</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      <ConfirmModal
        isOpen={actionModal.isOpen}
        onClose={() => setActionModal({ ...actionModal, isOpen: false })}
        onConfirm={handleAction}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        variant={modalConfig.variant as ModalVariant}
        loading={actionLoading}
        showInput={true}
        inputLabel="Причина"
        inputValue={actionModal.reason}
        onInputChange={(value) => setActionModal({ ...actionModal, reason: value })}
        inputPlaceholder="Въведете причина за действието..."
        inputRequired={true}
      />

      {/* Duration input for suspend action */}
      {actionModal.action === 'suspend' && actionModal.isOpen && (
        <div className="suspend-duration-input">
          <label>Продължителност (часове):</label>
          <input
            type="number"
            min="1"
            max="8760"
            value={actionModal.duration}
            onChange={(e) => setActionModal({ ...actionModal, duration: parseInt(e.target.value) || 72 })}
          />
        </div>
      )}
    </div>
  );
};

export default UsersManagementPage;
